import { action, createStandardAction } from 'typesafe-actions'
import { FeedbackTab } from '@chegg-wt/core-wa/constants/paraphraser'
import { ActionTypes } from '@chegg-wt/core-wa/state/constants'
import {
  Feedback,
  PaperStatus,
  PaperType,
  QuickActionPanelType,
} from '@chegg-wt/core-wa/types'
import { AppRouteCreators } from '@chegg-wt/core/types/core'
import { SentenceOffset, ToastType } from './types'

export const setParaphraseFlag = (paraphraseFlag: boolean) =>
  action(ActionTypes.SET_PARAPHRASE_FLAG, {
    paraphraseFlag,
  })

export const reviewPaper = createStandardAction(ActionTypes.RUN_PROOFREAD_CHECKS)<{
  routes: AppRouteCreators
}>()

export const setSentenceOffsets = (sentenceOffsets: SentenceOffset[]) => {
  // ! Tiptap Editor starts from 1 based index instead of zero
  const tiptapSentenceOffsets = sentenceOffsets.map(({ offset, ...rest }) => ({
    offset: offset + 1,
    ...rest,
  }))

  return action(ActionTypes.SET_SENTENCE_OFFSETS, {
    sentenceOffsets: tiptapSentenceOffsets,
  })
}

export const adjustSentenceOffsets = (sentenceOffsets: SentenceOffset[]) => {
  return action(ActionTypes.ADJUST_SENTENCE_OFFSETS, {
    sentenceOffsets,
  })
}

export const setFeedbacks = (feedbacks: Feedback[]) =>
  action(ActionTypes.SET_FEEDBACKS, {
    feedbacks,
  })

export const appendFeedback = (feedbacks: Feedback[]) =>
  action(ActionTypes.APPEND_TO_FEEDBACKS, {
    feedbacks,
  })

export const setIsPaperCheckInProgress = (isPaperCheckInProgress: boolean) =>
  action(ActionTypes.SET_IS_PAPER_CHECK_IN_PROGRESS, {
    isPaperCheckInProgress,
  })

export const setRewritePanelSentences = (rewritePanelSentences: SentenceOffset[]) =>
  action(ActionTypes.SET_REWRITE_PANEL_SENTENCES, {
    rewritePanelSentences,
  })
export const setQuickActionPanelType = (
  quickActionPanelType: QuickActionPanelType | null
) =>
  action(ActionTypes.SET_QUICK_ACTION_PANEL_TYPE, {
    quickActionPanelType,
  })

export const setActiveFeedbackTab = (activeFeedbackTab: FeedbackTab) =>
  action(ActionTypes.SET_ACTIVE_FEEDBACK_TAB, {
    activeFeedbackTab,
  })

export const setIsTimedOut = (isTimedOut: boolean) =>
  action(ActionTypes.SET_IS_TIMED_OUT, isTimedOut)

export const setActiveFeedbackCardId = (activeFeedbackCardId: string | null) =>
  action(ActionTypes.SET_ACTIVE_FEEDBACK_CARD_ID, {
    activeFeedbackCardId,
  })

export const deleteFeedback = (deletedFeedbackCardId: string) =>
  action(ActionTypes.DELETE_FEEDBACK, {
    feedbackId: deletedFeedbackCardId,
  })

export const updateTotalFeedbackCount = createStandardAction(
  ActionTypes.UPDATE_TOTAL_FEEDBACK_COUNT
)()
export const setTotalFeedbackCount = (totalFeedbackCount: number) =>
  action(ActionTypes.SET_TOTAL_FEEDBACK_COUNT, { totalFeedbackCount })

export const setPaperId = (paperId: string) =>
  action(ActionTypes.SET_PAPER_ID, { paperId })

export const setPaperTitle = (paperTitle: string) =>
  action(ActionTypes.SET_PAPER_TITLE, { paperTitle })

export const setPaperRequiresRecheck = (requiresReCheck: boolean) =>
  action(ActionTypes.SET_PAPER_REQUIRES_RECHECK, { requiresReCheck })

export const setIsTransferOfOwnershipInProgress = (
  isTransferOfOwnershipInProgress: boolean
) =>
  action(ActionTypes.SET_IS_TRANSFER_OF_OWNERSHIP_IN_PROGRESS, {
    isTransferOfOwnershipInProgress,
  })

export const updatePaperHandler = createStandardAction(ActionTypes.UPDATE_PAPER_HANDLER)()

export const setWordCount = (wordCount: number) =>
  action(ActionTypes.SET_WORD_COUNT, { wordCount })

export const notifyToast = (type: ToastType) => action(ActionTypes.NOTIFY_TOAST, { type })

export const clearToast = () => action(ActionTypes.CLEAR_TOAST)

export const setFetchNewSentencesLoading = (fetchNewSentencesLoading) =>
  action(ActionTypes.SET_FETCH_NEW_SENTENCES_LOADING, { fetchNewSentencesLoading })

export const setWritingAssitantPaperStatus = (paperStatus: PaperStatus) =>
  action(ActionTypes.SET_PAPER_STATUS, { paperStatus })

export const setUndoEnabled = (undoEnabled: boolean) =>
  action(ActionTypes.SET_UNDO_ENABLED, undoEnabled)
export const setRedoEnabled = (redoEnabled: boolean) =>
  action(ActionTypes.SET_REDO_ENABLED, redoEnabled)
export const setFeedbackReason = (reason: string, feedbackId: string) =>
  action(ActionTypes.SET_FEEDBACK_REASON, { reason, feedbackId })

export const proofReaderTransferOfOwnership = createStandardAction(
  ActionTypes.WRITING_ASSISTANT_TRANSFER_OF_OWNERSHIP
)<{ routes: AppRouteCreators }>()

export const setResetPaper = () => action(ActionTypes.RESET_PAPER)

export const setRequestForNewPaper = (isNewDocumentRequested: boolean) =>
  action(ActionTypes.NEW_DOC_CREATION_REQUEST, {
    isNewDocumentRequested,
  })

export const setPaperList = (paperList: PaperType[]) =>
  action(ActionTypes.SET_PAPER_LIST, {
    paperList,
  })

export const setIsLoggingOut = (isLoggingOut: boolean) =>
  action(ActionTypes.IS_LOGGING_OUT, {
    isLoggingOut,
  })
